import { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useTags } from '@/hooks/useTags';
import { useSubscription, SubscriptionTier } from '@/hooks/useSubscription';
import {
  OnboardingBackground,
  StepIdentity,
  StepIntention,
  StepMethod,
  StepSuccess,
  StepPaywall,
  focusOptions,
} from '@/components/onboarding';

/**
 * Onboarding is first-time only:
 * - Shown when profile.onboarding_completed is false (Index redirects here).
 * - After completion we set onboarding_completed = true and navigate to /app.
 * - Any later visit to /onboarding (logged in + completed) redirects to /app.
 * - Steps 1–3 work without auth; steps 4–5 require sign-in (redirect to Auth with return state).
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile();
  const { addTag } = useTags();
  const { loading: subscriptionLoading } = useSubscription();
  const stateStep = (location.state as { onboardingStep?: number } | null)?.onboardingStep;
  const [step, setStep] = useState(stateStep ?? 1);
  const [displayName, setDisplayName] = useState('');
  const [primaryFocus, setPrimaryFocus] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Resume at saved step when profile loads (only when NOT returning from Auth with explicit step)
  // Prefer location.state.onboardingStep so returning from sign-in lands on the correct step (e.g. 4)
  useEffect(() => {
    if (stateStep != null) return; // Auth or deep-link sent us to a specific step; don't overwrite
    if (!profile?.onboarding_completed && typeof profile?.onboarding_step === 'number') {
      const saved = profile.onboarding_step;
      if (saved >= 1 && saved <= 5) setStep(saved);
    }
  }, [stateStep, profile?.onboarding_completed, profile?.onboarding_step]);

  // Persist current step when user is logged in (so closing app preserves progress)
  useEffect(() => {
    if (user && step >= 1 && step <= 5 && profile && profile.onboarding_step !== step) {
      updateProfile({ onboarding_step: step }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateProfile is stable from useProfile
  }, [user, step, profile?.onboarding_step]);

  // Pre-fill display name if profile already has one
  useEffect(() => {
    if (profile?.display_name) {
      setDisplayName(profile.display_name);
    }
  }, [profile]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // When logged in, wait for profile/subscription and redirect if onboarding done
  if (user && (profileLoading || subscriptionLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // First-time only: once onboarding is completed, user never sees it again (redirect to app)
  if (user && profile?.onboarding_completed) {
    return <Navigate to="/app" replace />;
  }

  // Require auth for steps 4+; send to auth with return state so they resume here
  if (!user && step > 3) {
    return (
      <Navigate
        to="/auth"
        replace
        state={{ returnTo: '/onboarding', onboardingStep: step }}
      />
    );
  }

  const handleNext = () => {
    if (!user && step === 3) {
      navigate('/auth', { state: { returnTo: '/onboarding', onboardingStep: 4 }, replace: true });
      return;
    }
    const nextStep = step + 1;
    setStep(nextStep);
    // Persist step so user can resume if they leave mid-onboarding
    if (user && nextStep >= 1 && nextStep <= 5) {
      updateProfile({ onboarding_step: nextStep }).catch(() => {});
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Create tag for the selected focus
      if (primaryFocus) {
        const focusOption = focusOptions.find(f => f.id === primaryFocus);
        if (focusOption) {
          await addTag(focusOption.id, focusOption.color);
        }
      }

      // Update profile with onboarding data (but don't mark complete yet)
      await updateProfile({
        display_name: displayName.trim(),
        primary_focus: primaryFocus,
      });
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handlePaywallComplete = async (tier: SubscriptionTier) => {
    setIsCompleting(true);
    
    try {
      // Save profile data first if not already saved
      await handleSaveProfile();
      
      // Mark onboarding as completed
      await updateProfile({
        onboarding_completed: true,
      });

      navigate('/app', { replace: true, state: { firstVisitAfterOnboarding: true } });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setIsCompleting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 pb-12 pt-safe">
      <OnboardingBackground />
      
      {/* Progress indicator */}
      <div className="fixed top-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {[1, 2, 3, 4, 5].map((s) => (
          <div
            key={s}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              s === step 
                ? 'w-6 bg-primary' 
                : s < step 
                  ? 'bg-primary/50' 
                  : 'bg-muted-foreground/30'
            }`}
          />
        ))}
      </div>
      
      {/* Step content — single column, generous spacing so each step leads clearly to the next */}
      <div className="relative z-10 w-full max-w-lg mx-auto py-6 sm:py-10">
        <AnimatePresence mode="wait">
          {step === 1 && (
            <StepIdentity
              key="identity"
              displayName={displayName}
              setDisplayName={setDisplayName}
              onNext={handleNext}
            />
          )}
          
          {step === 2 && (
            <StepIntention
              key="intention"
              selectedFocus={primaryFocus}
              setSelectedFocus={setPrimaryFocus}
              onNext={handleNext}
            />
          )}
          
          {step === 3 && (
            <StepMethod
              key="method"
              onNext={handleNext}
            />
          )}
          
          {step === 4 && (
            <StepSuccess
              key="success"
              displayName={displayName || 'friend'}
              onComplete={handleNext}
              isLoading={false}
            />
          )}
          
          {step === 5 && (
            <StepPaywall
              key="paywall"
              onComplete={handlePaywallComplete}
              isLoading={isCompleting}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
